import { create } from 'zustand';
import {
  readForProject,
  readInitialPrefs,
  writeForProject,
  writeLastUsedOnly,
  type BoqViewPrefs,
} from '../lib/boqViewPrefs';

export type BoqDensity = 'compact' | 'standard' | 'comfortable';
export type CodeDisplayMode = 'code' | 'code_f';
/**
 * Način prikaza razdeljenih režij (overhead) v BoQ tabeli (T949):
 *  - 'off'   — brez režijskih stolpcev
 *  - 'costs' — stolpca "Razdeljeni stroški" + "Cena z overheadom"
 *  - 'unit'  — en stolpec "Cena z overhead/EM" (= total_with_overhead / quantity)
 */
export type OverheadView = 'off' | 'costs' | 'unit';
/**
 * Način prikaza prodajne cene v BoQ tabeli (T1133):
 *  - 'off'   — brez stolpca prodajne cene
 *  - 'total' — stolpec "Prodajna cena" (skupni znesek, = total_selling_price)
 *  - 'unit'  — stolpec "Prodajna cena/EM" (= total_selling_price / quantity)
 * Zrcali `OverheadView` — poenoten spustni meni v POGLED skupini.
 */
export type SellingView = 'off' | 'total' | 'unit';
/**
 * Način prikaza zneskov sekcij v drevesu (T1102):
 *  - 'sum'     — absolutni zneski sekcij/podsekcij (privzeto, kot doslej)
 *  - 'percent' — delež zneska sekcije glede na vrednost projekta (Σ / skupaj × 100)
 * Deluje v vseh treh prikazih drevesa (osnovni / jantarni overhead / vijolični scenarij).
 */
export type SectionAmountView = 'sum' | 'percent';

/**
 * T1155: nastavitve pogleda se pomnijo PO PROJEKTU (`lib/boqViewPrefs.ts`).
 * Store ostane skalaren — komponente berejo `sellingView` ipd. brez `projectId`
 * (odločitev T1114(j) spodaj) — per-projekt je le shramba, ki jo v store naloži
 * `hydrateProjectViews()` ob odprtju projekta.
 *
 * Vrednosti pred prvo hidracijo: tovarniške + podedljive zadnje uporabljene,
 * torej sta cenovna preklopa ob prvem paintu vedno `off`.
 */
const initialViewPrefs = readInitialPrefs();

/**
 * T2410 (1): v urejevalniku popisa ima stranska vrstica LASTNO izbiro (privzeto
 * skrčena na ikone), ločeno od globalne. Tabela potrebuje vsako slikovno piko —
 * pri 1280 px ji razširjena vrstica vzame 256 — drugod pa skrčena vrstica ni
 * potrebna. Ročni preklop v urejevalniku spremeni samo to izbiro in se zapomni
 * čez seje.
 */
export const EDITOR_SIDEBAR_STORAGE_KEY = 'bp-editor-sidebar-collapsed';

export function readEditorSidebarCollapsed(): boolean {
  try {
    return localStorage.getItem(EDITOR_SIDEBAR_STORAGE_KEY) !== 'false';
  } catch {
    return true;
  }
}

/** Zapiše spremembo v shrambo aktivnega projekta; brez projekta le v `lastUsed`. */
function persistViewPref(projectId: number | null, patch: Partial<BoqViewPrefs>): void {
  if (projectId === null) writeLastUsedOnly(patch);
  else writeForProject(projectId, patch);
}

interface UIState {
  isHelpOpen: boolean;
  toggleHelp: (force?: boolean) => void;
  isSidebarCollapsed: boolean;
  toggleSidebar: () => void;
  /** T2410: izbira stranske vrstice na poti `/projects/:id/boq` (privzeto skrčena). */
  isEditorSidebarCollapsed: boolean;
  toggleEditorSidebar: () => void;
  isSidebarHidden: boolean;
  setSidebarHidden: (value: boolean) => void;
  /**
   * T1263: pod `md` je stranska vrstica privzeto skrita in se odpre kot
   * prekrivni (overlay) meni prek hamburger gumba v glavi. Ločeno od
   * `isSidebarCollapsed` (desktop strni/razširi) in `isSidebarHidden`
   * (maximize/zen) — mobilni overlay ne sme vplivati na desktop stanje.
   */
  isMobileSidebarOpen: boolean;
  setMobileSidebarOpen: (value: boolean) => void;
  /**
   * Zen Mode (T805): skrije globalni Sidebar + Topbar IN BoQ stran header, da
   * editor dobi maksimalen prostor. Toggla se s F9 ali vidnim gumbom v BoQ
   * toolbaru. Ločen od `isSidebarHidden` (ki ga uporablja "maximize" toggle za
   * desno inspector ploščo), da se gumba ne prepletata.
   */
  isZenMode: boolean;
  toggleZenMode: (force?: boolean) => void;
  isTestPanelOpen: boolean;
  toggleTestPanel: (force?: boolean) => void;
  isModuleTrialModalOpen: boolean;
  trialModuleSlug: string | null;
  openModuleTrial: (slug: string) => void;
  closeModuleTrial: () => void;
  notificationPanelOpen: boolean;
  toggleNotificationPanel: (force?: boolean) => void;
  isDarkMode: boolean;
  toggleDarkMode: () => void;
  boqDensity: BoqDensity;
  setBoqDensity: (density: BoqDensity) => void;
  codeDisplayMode: CodeDisplayMode;
  setCodeDisplayMode: (mode: CodeDisplayMode) => void;
  showItemDescription: boolean;
  setShowItemDescription: (show: boolean) => void;
  /** T2429: prikaz slik detajlov pod postavkami (po projektu, privzeto vklopljen). */
  showDetailImages: boolean;
  setShowDetailImages: (show: boolean) => void;
  /**
   * T1068: preklop prikaza prodajne cene (namesto lastne) v BoQ tabeli.
   * T1114 (j): store namerno ostaja SKALAREN (eno polje `sellingView`, ne
   * `Record<projectId, …>`) — flag berejo tudi komponente brez projektnega
   * konteksta (SortableItemRow, SortableNoteRow neposredno iz uiStore);
   * per-projekt vrednost NEPOSREDNO v store-u bi zahtevala prop-drilling
   * `projectId` skozi vrstice.
   * T1133: iz bool preklopa v tristanjski enum (off/total/unit) — poenoten
   * spustni meni z režijami. `showSellingPrice` izpeljemo kot `sellingView !== 'off'`.
   * T1155: persistenca JE per-projekt (`lib/boqViewPrefs.ts` →
   * `boq-view-prefs.byProject`) — le OBLIKA store-a ostaja skalarna iz zgornjega
   * razloga; `hydrateProjectViews()` ob odprtju projekta prepiše to eno polje
   * z vrednostjo za `activeViewProjectId`.
   */
  sellingView: SellingView;
  setSellingView: (view: SellingView) => void;
  inspectorAuditExpanded: boolean;
  setInspectorAuditExpanded: (expanded: boolean) => void;
  overheadView: OverheadView;
  setOverheadView: (view: OverheadView) => void;
  sectionAmountView: SectionAmountView;
  setSectionAmountView: (view: SectionAmountView) => void;
  /**
   * T1155: projekt, katerega nastavitve pogleda so trenutno naložene v store.
   * `null` = pred prvo hidracijo (setterji takrat pišejo samo `lastUsed`).
   */
  activeViewProjectId: number | null;
  /** Naloži nastavitve pogleda danega projekta v store; kliče jo BoQ editor stran ob odprtju projekta. */
  hydrateProjectViews: (projectId: number) => void;
}

export const useUIStore = create<UIState>((set, get) => ({
  isHelpOpen: false,
  toggleHelp: (force) =>
    set((state) => ({
      isHelpOpen: force !== undefined ? force : !state.isHelpOpen,
    })),
  isSidebarCollapsed: false,
  toggleSidebar: () =>
    set((state) => ({ isSidebarCollapsed: !state.isSidebarCollapsed })),
  isEditorSidebarCollapsed: readEditorSidebarCollapsed(),
  toggleEditorSidebar: () =>
    set((state) => {
      const next = !state.isEditorSidebarCollapsed;
      try {
        localStorage.setItem(EDITOR_SIDEBAR_STORAGE_KEY, String(next));
      } catch {
        // Shramba ni dosegljiva (zasebno okno) — izbira velja do konca seje.
      }
      return { isEditorSidebarCollapsed: next };
    }),
  isSidebarHidden: false,
  setSidebarHidden: (value) => set({ isSidebarHidden: value }),
  isMobileSidebarOpen: false,
  setMobileSidebarOpen: (value) => set({ isMobileSidebarOpen: value }),
  isZenMode: false,
  toggleZenMode: (force) =>
    set((state) => ({
      isZenMode: force !== undefined ? force : !state.isZenMode,
    })),
  isTestPanelOpen: false,
  toggleTestPanel: (force) =>
    set((state) => ({
      isTestPanelOpen: force !== undefined ? force : !state.isTestPanelOpen,
    })),
  isModuleTrialModalOpen: false,
  trialModuleSlug: null,
  openModuleTrial: (slug) => set({ isModuleTrialModalOpen: true, trialModuleSlug: slug }),
  closeModuleTrial: () => set({ isModuleTrialModalOpen: false, trialModuleSlug: null }),
  notificationPanelOpen: false,
  toggleNotificationPanel: (force) =>
    set((state) => ({
      notificationPanelOpen: force !== undefined ? force : !state.notificationPanelOpen,
    })),
  isDarkMode: localStorage.getItem('theme') === 'dark' ||
    (localStorage.getItem('theme') === null && window.matchMedia('(prefers-color-scheme: dark)').matches),
  toggleDarkMode: () =>
    set((state) => {
      const next = !state.isDarkMode;
      localStorage.setItem('theme', next ? 'dark' : 'light');
      return { isDarkMode: next };
    }),
  boqDensity: initialViewPrefs.boqDensity,
  setBoqDensity: (density) => {
    persistViewPref(get().activeViewProjectId, { boqDensity: density });
    set({ boqDensity: density });
  },
  codeDisplayMode: initialViewPrefs.codeDisplayMode,
  setCodeDisplayMode: (mode) => {
    persistViewPref(get().activeViewProjectId, { codeDisplayMode: mode });
    set({ codeDisplayMode: mode });
  },
  showItemDescription: initialViewPrefs.showItemDescription,
  setShowItemDescription: (show) => {
    persistViewPref(get().activeViewProjectId, { showItemDescription: show });
    set({ showItemDescription: show });
  },
  showDetailImages: initialViewPrefs.showDetailImages,
  setShowDetailImages: (show) => {
    persistViewPref(get().activeViewProjectId, { showDetailImages: show });
    set({ showDetailImages: show });
  },
  sellingView: initialViewPrefs.sellingView,
  setSellingView: (view) => {
    persistViewPref(get().activeViewProjectId, { sellingView: view });
    set({ sellingView: view });
  },
  inspectorAuditExpanded: localStorage.getItem('boq-inspector-audit-expanded') === 'true',
  setInspectorAuditExpanded: (expanded) => {
    localStorage.setItem('boq-inspector-audit-expanded', String(expanded));
    set({ inspectorAuditExpanded: expanded });
  },
  overheadView: initialViewPrefs.overheadView,
  setOverheadView: (view) => {
    persistViewPref(get().activeViewProjectId, { overheadView: view });
    set({ overheadView: view });
  },
  sectionAmountView: initialViewPrefs.sectionAmountView,
  setSectionAmountView: (view) => {
    persistViewPref(get().activeViewProjectId, { sectionAmountView: view });
    set({ sectionAmountView: view });
  },
  activeViewProjectId: null,
  hydrateProjectViews: (projectId) =>
    set({ ...readForProject(projectId), activeViewProjectId: projectId }),
}));
